/**
 * Bob Laadt Op — bob-plug-options ("Gedeeld of eigen stopcontact?").
 * Bron: _design/Bob-laadt-op-thomas/werkbestanden/paginas/hoe-werkt-het.html r. 2665–2728
 * (+ `stilstaand` r. 2406).
 *
 * 1. Schakelaar (aria-pressed): licht de kaart(en) op die bij het antwoord passen.
 *    Elke kaart draagt data-match: "off" past als de schakelaar uit staat, "on" als hij aan staat.
 * 2. Fotocarrousel per kaart: dia's kruisen over elkaar heen. Loopt zelf rond (5 s) tot je hem
 *    bedient; daarna blijft hij staan op jouw foto. Pauze bij hover/focus, start pas als hij
 *    voor 35% in beeld is, staat stil bij prefers-reduced-motion.
 *    De stippen maakt dit script; hun aria-label komt uit data-dot-label ("… [n] … [total]").
 *
 * Custom element, zodat het na een re-render in de theme editor opnieuw werkt.
 */
class BobPlugOptions extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.stilstaand = matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.cards = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.pv-card')));

    /* ── Schakelaar ── */
    const knop = this.querySelector('[data-pv-toggle]');
    knop?.addEventListener(
      'click',
      () => {
        const aan = knop.getAttribute('aria-pressed') !== 'true';
        knop.setAttribute('aria-pressed', String(aan));
        this.sync(aan);
      },
      { signal }
    );
    if (knop) this.sync(knop.getAttribute('aria-pressed') === 'true');

    /* ── Carrousels ── */
    /** @type {Map<Element, {stop: () => void, destroy: () => void}>} */
    this.carousels = new Map();
    this.querySelectorAll('[data-carousel]').forEach((car) => {
      this.carousels.set(car, this.carousel(/** @type {HTMLElement} */ (car), signal));
    });

    /* Theme editor: een kaart kiezen zet zijn carrousel stil, zodat hij niet onder je wegwisselt. */
    this.addEventListener(
      'shopify:block:select',
      (e) => {
        const card = /** @type {HTMLElement} */ (e.target).closest?.('.pv-card');
        const car = card?.querySelector('[data-carousel]');
        if (car) this.carousels.get(car)?.stop();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.carousels?.forEach((c) => c.destroy());
    this.carousels?.clear();
  }

  /** @param {boolean} aan — staat de schakelaar aan? */
  sync(aan) {
    this.cards.forEach((card) => {
      card.classList.toggle('is-match', (card.dataset.match === 'on') === aan);
    });
  }

  /**
   * @param {HTMLElement} car
   * @param {AbortSignal} signal
   */
  carousel(car, signal) {
    const dias = Array.from(car.querySelectorAll('.pv-slide'));
    const dots = /** @type {HTMLElement|null} */ (car.querySelector('.pv-dots'));
    const leeg = { stop() {}, destroy() {} };
    if (dias.length < 2 || !dots) return leeg;

    let i = Math.max(0, dias.findIndex((d) => d.classList.contains('is-active')));
    let loopt = !this.stilstaand;
    let inBeeld = false;
    /** @type {number|undefined} */
    let timer;

    const labelSjabloon = dots.dataset.dotLabel || '';
    dias.forEach((_, n) => {
      const b = document.createElement('button');
      b.className = 'pv-dot';
      b.type = 'button';
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-label', labelSjabloon.replace('[n]', String(n + 1)).replace('[total]', String(dias.length)));
      b.addEventListener('click', () => { stop(); toon(n); }, { signal });
      dots.append(b);
    });
    const stippen = Array.from(dots.children);

    /** @param {number} n */
    const toon = (n) => {
      i = (n + dias.length) % dias.length;
      dias.forEach((d, k) => d.classList.toggle('is-active', k === i));
      stippen.forEach((d, k) => {
        d.setAttribute('aria-current', String(k === i));
        d.setAttribute('aria-selected', String(k === i));
      });
    };
    const pauze = () => clearInterval(timer);
    const stop = () => { loopt = false; pauze(); };
    const start = () => {
      pauze();
      if (loopt && inBeeld) timer = window.setInterval(() => toon(i + 1), 5000);
    };

    car.querySelector('.pv-nav--prev')?.addEventListener('click', () => { stop(); toon(i - 1); }, { signal });
    car.querySelector('.pv-nav--next')?.addEventListener('click', () => { stop(); toon(i + 1); }, { signal });
    /* Stil zolang je erover hangt of erin tabt: anders wisselt de foto terwijl je het bijschrift leest. */
    car.addEventListener('pointerenter', pauze, { signal });
    car.addEventListener('pointerleave', start, { signal });
    car.addEventListener('focusin', pauze, { signal });
    car.addEventListener('focusout', start, { signal });

    toon(i);

    /* Pas gaan lopen als de kaart in beeld komt. */
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            inBeeld = true;
            start();
            io.disconnect();
          }
        }
      },
      { threshold: 0.35 }
    );
    io.observe(car);

    return {
      stop,
      destroy() {
        pauze();
        io.disconnect();
      },
    };
  }
}

if (!customElements.get('bob-plug-options')) {
  customElements.define('bob-plug-options', BobPlugOptions);
}
